// Types
import {
  ISyntheticConfig,
  ISyntheticSearchParams,
  ISyntheticPurchaseTableReport,
  ISynthaticOrderbooksBody,
  ISyntheticPairPositionTableReport,
  IPairPositionSearchParams,
  IMinProfit,
  IFilterParams,
  IConversionAccountTableReport,
  IConversionAccountSearchParam,
  IAccount,
  SyntheticApiTab,
} from "src/Interfaces/Synthetic";
import { Report } from "src/Interfaces/TableInterfaces";
import { Packet } from "src/Interfaces/commonDtos";
// Utilities
import {
  getRequest,
  getTable,
  postDownloadExcel,
  postRequest,
  putRequest,
  deleteRequest,
} from "./requestTemplates";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { AxiosRequestConfig } from "axios";

const getSyntheticTabQuery = (tab?: SyntheticApiTab | string | number | null) =>
  tab === "EntryPairPosition" || tab === "VerticalSpread"
    ? `tab=${encodeURIComponent(tab)}`
    : "";

const appendSyntheticTabQuery = (
  url: string,
  tab?: SyntheticApiTab | string | number | null,
) => {
  const query = getSyntheticTabQuery(tab);
  if (!query) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${query}`;
};

/**
 * Market Watch => Synthatic
 * دانلود اکسل
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getTableExcel = (
  type: "sell" | "buy",
  searchParam: ISyntheticSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/synthetic/${
      type === "sell" ? "getSellTableExcel" : "getPurchaseTableExcel"
    }?${rand}`,
    searchParam,
  );
};

/**
 * Market Watch => Synthatic => buyOrSell
 * حدول خرید
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getPurchaseTable = (
  searchParam: ISyntheticSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ISyntheticPurchaseTableReport>> =>
  getTable<ISyntheticSearchParams, ISyntheticPurchaseTableReport>(
    "/v1/synthetic/getPurchaseTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => Synthatic => buyOrSell
 * حدول فروش
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getSellTable = (
  searchParam: ISyntheticSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ISyntheticPurchaseTableReport>> =>
  getTable<ISyntheticSearchParams, ISyntheticPurchaseTableReport>(
    "/v1/synthetic/getSellTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * حدول های کوچک خرید و فروش در جفت پوزیشن مصنوعی
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getSynthaticOrderbooks = (
  body: ISynthaticOrderbooksBody,
): Promise<any> => postRequest("/v1/synthetic/getOrderbook", body);

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * دارپ داون نوع جفت در قسمت جستجو
 *
 * @async
 * @version 1.4.0
 */
export const getPairType = (
  tab?: SyntheticApiTab | string | number | null,
): Promise<any[]> =>
  getRequest(appendSyntheticTabQuery("/v1/synthetic/getPairTypeDropdown", tab));

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * دارپ داون نوع پایه در قسمت جستجو
 *
 * @async
 * @version 1.4.0
 */
export const getBaseType = (): Promise<any[]> =>
  getRequest("/v1/synthetic/getBaseTypeDropdown");

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * جدول جفت پوزیشن مصنوعی
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getPairPositionTable = (
  searchParam: IPairPositionSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
  tab?: SyntheticApiTab,
): Promise<Report<ISyntheticPairPositionTableReport>> =>
  getTable<IPairPositionSearchParams, ISyntheticPairPositionTableReport>(
    appendSyntheticTabQuery("/v1/synthetic/artificialPairPositionTable", tab),
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * دانلود فایلاکسل جفت پوزیشن
 *
 * @param {IPairPositionSearchParams} data
 * @async
 * @version 1.4.0
 */
export const getPaiPositionExcel = (
  searchParam: IPairPositionSearchParams,
  tab?: SyntheticApiTab,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    appendSyntheticTabQuery(
      `/v1/synthetic/artificialPairPositionTableExcel?${rand}`,
      tab,
    ),
    searchParam,
  );
};
/**
 * Market Watch => Synthatic => Artificial Pair Position
 * گرفتن مقادیر پیشفرض تنظیمات جفت پوزیشن
 *
 * @async
 * @version 1.4.0
 */
export const getPairPositionConfig = (
  tab?: SyntheticApiTab,
): Promise<ISyntheticConfig> =>
  getRequest(appendSyntheticTabQuery("/v1/synthetic/config", tab));

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * ست کردن مقادیر پیشفرض تنظیمات جفت پوزیشن
 *
 * @param {ISyntheticConfig} data
 * @async
 * @version 1.4.0
 */
export const setPairPositionConfig = (
  config: ISyntheticConfig,
  tab?: SyntheticApiTab,
): Promise<Packet<boolean>> =>
  postRequest(appendSyntheticTabQuery("/v1/synthetic/config", tab), config);

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * دکمه بازده انتظاری
 *
 * @async
 * @version 1.4.0
 */
export const getMinProfit = (tab?: SyntheticApiTab): Promise<IMinProfit[]> =>
  getRequest(appendSyntheticTabQuery("/v1/synthetic/GetMinProfit", tab));

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * ست کردن مدال بازده انتظاری
 *
 * @param {IMinProfit[]} data
 * @async
 * @version 1.4.0
 */
export const setMinProfit = (
  config: IMinProfit[],
  tab?: SyntheticApiTab,
): Promise<Packet<boolean>> =>
  postRequest(appendSyntheticTabQuery("/v1/synthetic/SetMinProfit", tab), config);

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * گرفتن تنظیمات فیلتر
 *
 * @async
 * @version 1.4.0
 */
export const getFilterSetting = (tab?: SyntheticApiTab): Promise<IFilterParams> =>
  getRequest(appendSyntheticTabQuery("/v1/synthetic/getDefaultFilterConfig", tab));

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * ثبت تنظیمات فیلتر
 *
 * @param {ISyntheticConfig} data
 * @async
 * @version 1.4.0
 */
export const setFilterSetting = (
  config: IFilterParams,
  tab?: SyntheticApiTab,
): Promise<Packet<boolean>> =>
  putRequest(
    appendSyntheticTabQuery("/v1/synthetic/setDefaultFilterConfig", tab),
    config,
  );

//------------------اکانت کانورژن-------------------
/**
 * Market Watch => Synthatic => conversion account
 * حدول اکانت کانورزن
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getConversionAccountsTable = (
  searchParam: IConversionAccountSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IConversionAccountTableReport>> =>
  getTable<IConversionAccountSearchParam, IConversionAccountTableReport>(
    "/v1/ConversionAccount/accountTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => Synthatic => conversion account
 * حذف اکانت
 *
 * @param {number} id
 * @async
 * @version 1.4.0
 */
export const deleteConversionAccount = (id: number): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/ConversionAccount/delete?id=${id}`);

/**
 * Market Watch => Synthatic => conversion account
 * افزودن اکانت
 *
 * @param {IAccount} data
 * @async
 * @version 1.4.0
 */
export const addAccount = (data: IAccount): Promise<Packet<boolean>> =>
  postRequest("/v1/ConversionAccount/AddAccount", data);

/**
 * Market Watch => Synthatic => conversion account
 * ویرایش اکانت
 *
 * @param {IAccount} data
 * @async
 * @version 1.4.0
 */
export const editAccount = (data: IAccount): Promise<Packet<boolean>> =>
  putRequest("/v1/ConversionAccount/EditAccount", data);

/**
 * Market Watch => Synthatic => Artificial Pair Position
 * گرفتن لیست اکانت‌ها
 *
 * @async
 * @version 1.4.0
 */
export const getAccontsDropdown = (): Promise<any[]> =>
  getRequest("/v1/ConversionAccount/ConversionAccountDropdown");

//---------------------کانورژن آفست----------------------------
/**
 * Market Watch => Synthatic => conversion offset
 * حدول اکانت کانورزن
 *
 * @param {ITabelParams} data
 * @async
 * @version 1.4.0
 */
export const getConversionOffsetTable = (
  searchParam: IPairPositionSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<ISyntheticPairPositionTableReport>> =>
  getTable<IPairPositionSearchParams, ISyntheticPairPositionTableReport>(
    "/v1/ConversionOffset/offsetTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

/**
 * Market Watch => Synthatic => conversion offset
 * دانلود فایل اکسل
 *
 * @param {IPairPositionSearchParams} data
 * @async
 * @version 1.4.0
 */
export const getConversionOffsetExcel = (
  searchParam: IPairPositionSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ConversionOffset/offsetExcel?${rand}`,
    searchParam,
  );
};

/**
 * Covered Call => Offset
 * تنظیمات فیلتر
 *
 * @async
 * @version 1.4.0
 */
export const getOffsetFilterSetting = (): Promise<IPairPositionSearchParams> =>
  getRequest("/v1/ConversionOffset/getpreset");

/**
 * Covered Call => Offset
 * ثیت فیلتر
 *
 * @param {IRollFilterParams} config
 * @param {string} riskProfileId
 * @async
 * @version 1.4.0
 */
export const setOffsetFilterSetting = (
  config: IPairPositionSearchParams,
): Promise<Packet<boolean>> =>
  putRequest("/v1/ConversionOffset/setpreset", config);
